/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";

import creditMappingTableData from "Views/Activities/CreditMapping/data/creditMappingTableData";
import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";
import { useState, useEffect } from "react";
import { getAllRaiseInvoices } from "services/financeService";

import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import Dropdown from "components/CustomDropdown/Dropdown";
import { Checkbox, FormControlLabel } from "@mui/material";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import PaginationBar from "components/PaginationBar/PaginationBar";

function Tables() {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
  });
  const [statusCounts, setStatusCounts] = useState({
    ALL: 0,
    PAID: 0,
    PENDING: 0,
    FAILED: 0,
    VOID: 0,
  });
  const [activeTab, setActiveTab] = useState("All");
  const [completedClassesRange, setCompletedClassesRange] = useState("");
  const [dueClassesOnly, setDueClassesOnly] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false);
  };

  // Fetch invoices from API
  const fetchInvoices = async (page = 1, limit = rowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit,
      };

      // Add status filter if not 'All'
      if (activeTab !== "All") {
        params.status = activeTab.toUpperCase();
      }

      const response = await getAllRaiseInvoices(params);

      if (response.success) {
        setInvoiceData(response.data || []);
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalRecords: 0,
            limit: limit,
          }
        );
        setStatusCounts(
          response.statusCounts || {
            ALL: 0,
            PAID: 0,
            PENDING: 0,
            FAILED: 0,
            VOID: 0,
          }
        );
      }
    } catch (err) {
      setError(err.message || "Failed to fetch invoices");
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when activeTab changes
  useEffect(() => {
    fetchInvoices();
  }, [activeTab]);

  // Handle tab change
  const handleTabChange = (tabLabel) => {
    setActiveTab(tabLabel);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchInvoices(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = event.target.value;
    setRowsPerPage(newRowsPerPage);
    fetchInvoices(1, newRowsPerPage); // Reset to first page
  };

  const { columns, rows } = creditMappingTableData(handleDeleteClick, invoiceData);

  // Dynamic tabs data from API statusCounts
  const tabsData = [
    { label: "All", count: statusCounts.ALL || 0, color: "#2027e1ff", content: "" },
    { label: "Paid", count: statusCounts.PAID || 0, color: "#10a6ecff", content: "" },
    { label: "Pending", count: statusCounts.PENDING || 0, color: "#ec3c10ff", content: "" },
    { label: "Failed", count: statusCounts.FAILED || 0, color: "#ec10c7ff", content: "" },
    { label: "Void", count: statusCounts.VOID || 0, color: "#1a6808b7", content: "" },
  ];

  const handleStatusChange = (event) => {
    setCompletedClassesRange(event.target.value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Credit Mapping List
              </ArgonTypography>

              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs
                    tabsData={tabsData}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                  />
                </ArgonBox>

                <ArgonBox>
                  <FormControlLabel
                    label="Due Classes"
                    control={
                      <Checkbox
                        color="primary"
                        checked={dueClassesOnly}
                        onChange={(e) => setDueClassesOnly(e.target.checked)}
                      />
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",

                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.85rem", // smaller font
                        fontWeight: 600,
                      },
                    }}
                  />
                </ArgonBox>

                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <ArgonTypography
                    variant="caption"
                    sx={{ whiteSpace: "nowrap" }}
                    fontWeight="medium"
                  >
                    Completed classes
                  </ArgonTypography>

                  <Dropdown
                    label=""
                    value={completedClassesRange}
                    onChange={handleStatusChange}
                    options={[
                      "01-10%",
                      "11-20%",
                      "21-30%",
                      "31-40%",
                      "41-50%",
                      "51-60%",
                      "61-70%",
                      "71-80%",
                      "81-90%",
                      "91-100%",
                    ]}
                    width={120}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching client mappings...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : error ? (
                <ArgonBox p={3} textAlign="center">
                  <ArgonTypography variant="body2" color="error">
                    {error}
                  </ArgonTypography>
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {activeTab === "All"
                      ? "No Credit Mapping found"
                      : `No ${activeTab.toLowerCase()} credit mappings found`}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {activeTab === "All"
                      ? "Try adjusting your filters or check back later."
                      : `No credit mappings with ${activeTab.toLowerCase()} status found.`}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>

            {pagination.totalRecords > 0 && !loading && !error && (
              <PaginationBar
                currentPage={pagination.currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={pagination.totalRecords}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>

        <DeleteDialog
          open={openDeleteDialog}
          onClose={handleCloseDialog}
          onConfirm={handleDeleteClick}
          title="Delete Credit Mapping"
          description="Are you sure you want to delete this credit mapping?"
          cancelText="Cancel"
          confirmText="Delete Credit Mapping"
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
